<template>
  <nav
    v-if="nbPages > 1"
    class="nfb-pagination"
    ref="paginationLists"
  >
    <ul
      class="nfb-pagination__container"
      ref="paginationControlsFirst"
    >
      <li
        v-if="showGoFirst"
        :class="['nfb-pagination__element', 'nfb-pagination__previous-btn', {'nfb-pagination__element--disabled': !firstPage && !this.goFirst === 'auto'}]"
      >
        <button
          @click.prevent="goToPage(firstPage)"
          :aria-label="i18n[locale].goToFirst"
        >
          <i class="nfb-icon icon-chevron-left" />
        </button>
      </li>
      <li :class="['nfb-pagination__element', 'nfb-pagination__previous-btn',  {'nfb-pagination__element--disabled': !previousPage }]"
      >
        <button
          @click.prevent="goToPage(previousPage)"
          :aria-label="i18n[locale].previous"
        >
          <i class="nfb-icon icon-prev" />
        </button>
      </li>
    </ul>
    <ul class="nfb-pagination__container">
      <li
        v-for="index in arrayWithPagesNumbers"
        :key="index"
        class="nfb-pagination__element"
        :style="pageElementStyle"
      >
        <button
          :class="['nfb-pagination__page', {'nfb-pagination__page--active': index === currentPage}]"
          @click.prevent="goToPage(index)"
          v-html="index"
        />
      </li>
    </ul>
    <ul
      class="nfb-pagination__container"
      ref="paginationControlsLast"
    >
      <li :class="['nfb-pagination__element', 'nfb-pagination__next-btn', {'nfb-pagination__element--disabled': !nextPage}]">
        <button
          @click.prevent="goToPage(nextPage)"
          :aria-label="i18n[locale].next"
        >
          <i class="nfb-icon icon-next" />
        </button>
      </li>
      <li
        v-if="showGoLast"
        :class="['nfb-pagination__element', 'nfb-pagination__next-btn', {'nfb-pagination__element--disabled': !lastPage && !this.goLast === 'auto'}]"
      >
        <button
          @click.prevent="goToPage(lastPage)"
          :aria-label="i18n[locale].goToLast"
        >
          <i class="nfb-icon icon-chevron-right" />
        </button>
      </li>
    </ul>
  </nav>
</template>

<script>
import enTranslations from '../../i18n/en_CA/pagination.json'
import frTranslations from '../../i18n/fr_CA/pagination.json'
import throttle from 'lodash/throttle'

export default {
  name: 'Pagination',
  props: {
    locale: {
      type: String,
      default: 'en'
    },
    nbPages: {
      type: Number,
      required: true
    },
    currentPage: {
      type: Number,
      default: 1
    },
    pageElementPixelsWidth: {
      type: Number,
      default: 80
    },
    goFirst: {
      type: [Boolean, String],
      default: false,
      validator (value) {
        return value instanceof Boolean || value === 'auto'
      }
    },
    goLast: {
      type: [Boolean, String],
      default: false,
      validator (value) {
        return value instanceof Boolean || value === 'auto'
      }
    }
  },
  data: function () {
    return {
      showGoFirst: false,
      showGoLast: false,
      arrayWithPagesNumbers: [],
      throttledWatchResize: throttle(this.watchResize, 200),
      i18n: {
        fr: frTranslations,
        en: enTranslations
      }
    }
  },
  created () {
    if (this.goFirst !== 'auto') this.showGoFirst = this.goFirst
    if (this.goLast !== 'auto') this.showGoLast = this.goLast
  },
  mounted () {
    window.addEventListener('resize', this.throttledWatchResize)
    this.updatePagesArray()
  },
  unmounted () {
    window.removeEventListener('resize', this.throttledWatchResize)
  },
  computed: {
    firstPage () {
      if (this.currentPage === 1) {
        return false
      }
      return 1
    },
    lastPage () {
      if (this.currentPage === this.nbPages) {
        return false
      }
      return this.nbPages
    },
    previousPage () {
      if (this.currentPage <= 1) {
        return false
      }
      return this.currentPage - 1
    },
    nextPage () {
      if (this.currentPage >= this.nbPages) {
        return false
      }
      return this.currentPage + 1
    },
    pageElementStyle () {
      return { width: `${this.pageElementPixelsWidth}px` }
    }
  },
  methods: {
    watchResize () {
      this.updatePagesArray()
    },
    updatePagesArray () {
      const containerWidth = this.getPaginationContainerAvailableWidth()
      if (containerWidth) {
        const amountVisible = Math.min(Math.floor(containerWidth / this.pageElementPixelsWidth), this.nbPages)
        const threshold = Math.floor(amountVisible / 2)
        const templateArray = amountVisible > 0 ? Array.from(Array(amountVisible).keys(), item => item + 1) : []

        const newArray = templateArray.map((value) => {
          if (amountVisible === 1) {
            return this.currentPage
          } else if (this.currentPage <= amountVisible) {
            return value
          } else if (this.currentPage + threshold >= this.nbPages) {
            return this.nbPages - (amountVisible - value)
          } else {
            return this.currentPage + (value - threshold)
          }
        })

        if (this.goFirst === 'auto') this.showGoFirst = !newArray.includes(1) && this.currentPage !== 1
        if (this.goLast === 'auto') this.showGoLast = !newArray.includes(this.nbPages) && this.currentPage !== this.nbPages

        this.arrayWithPagesNumbers = newArray
      }
    },
    getPaginationContainerAvailableWidth () {
      const { paginationLists, paginationControlsFirst, paginationControlsLast } = this.$refs
      return paginationLists?.getBoundingClientRect().width -
        paginationControlsFirst?.getBoundingClientRect().width -
        paginationControlsLast?.getBoundingClientRect().width
    },
    goToPage (n) {
      this.$emit('goToPage', n)
    }
  },
  watch: {
    currentPage: {
      handler () {
        this.updatePagesArray()
      }
    },
    nbPages: {
      handler () {
        this.updatePagesArray()
      }
    }
  }
}
</script>

<style lang="sass">
@import '@web-nfb/frontend-static/design-system/sass/elements/pagination'
</style>
